import dayjs from 'dayjs';
import { PlanTypes } from '../types/HomePage.type';
import { MeClinic } from '../types/Me.type';

/**
 * How many days after their subscription start an "Essentials" (paid) clinic
 * keeps seeing the TTFV onboarding experience. Trials are unaffected by this.
 */
export const TTFV_ESSENTIALS_WINDOW_DAYS = 90;

/**
 * "Essentials" plans — the paid, non-EHR tiers that get the TTFV experience
 * during their first-value window. EHR and education are excluded.
 */
const TTFV_ESSENTIALS_PLANS: readonly string[] = [
  PlanTypes.basic,
  PlanTypes.plus,
  PlanTypes.premium,
];

/**
 * Whether a clinic should see the TTFV ("Time To First Value") onboarding
 * experience — the trial-style home, checklist, and tours.
 *
 * Eligible when:
 *  - the clinic is on the `trial` plan (covers trials and reactivations), OR
 *  - the clinic is on an Essentials plan (`basic` / `plus` / `premium`) and its
 *    subscription began within the last {@link TTFV_ESSENTIALS_WINDOW_DAYS} days.
 *    This is what lets direct/seminar signups — who bypass the trial and land
 *    straight on a paid plan — get the onboarding experience Chameleon used to
 *    serve, while established subscribers (start date > 90 days) never see it.
 *
 * Regardless of plan or window, a clinic whose TTFV expired by completion
 * (`ttfvExpiredAt` stamped once the Getting Started checklist is done and 20
 * condition reports are prescribed — see the trial-checklist endpoint) is
 * never eligible again (13104227261).
 */
export const isEligibleForTTFV = (
  clinic: Pick<MeClinic, 'plan' | 'subscriptionStartDate' | 'ttfvExpiredAt'> | null | undefined
): boolean => {
  if (clinic?.ttfvExpiredAt) return false;

  const plan = clinic?.plan;
  if (!plan) return false;

  if (plan === PlanTypes.trial) return true;

  if (!TTFV_ESSENTIALS_PLANS.includes(plan)) return false;

  const { subscriptionStartDate } = clinic;
  if (!subscriptionStartDate) return false;

  // Compare date-only (slice off any time component) so a clinic near the
  // boundary can't be pushed across midnight by timezone offset.
  const start = dayjs(String(subscriptionStartDate).slice(0, 10));
  if (!start.isValid()) return false;

  const daysSinceStart = dayjs().startOf('day').diff(start, 'day');

  // "At 90 days from the subscription start date, the TTFV/Kickstart
  // experience expires" (13104227261) — day 90 itself is expired, so the
  // window is [0, 90), not [0, 90].
  return daysSinceStart >= 0 && daysSinceStart < TTFV_ESSENTIALS_WINDOW_DAYS;
};

/**
 * Within the TTFV experience, whether the clinic is a genuine trial (no paid
 * plan chosen yet) vs. a paid Essentials clinic. Reliable because `/me` only
 * reports `plan === 'trial'` for clinics with no active paid subscription — any
 * clinic with a Stripe subscription keeps its real Essentials tier.
 *
 * Use this to gate trial-only surfaces (the "Select my plan" CTA, the trial
 * countdown pill) so paid clinics that now see the TTFV home don't get prompted
 * to pick a plan they already have.
 */
export const isTtfvTrial = (clinic: Pick<MeClinic, 'plan'> | null | undefined): boolean =>
  clinic?.plan === PlanTypes.trial;
